/**
 * Incremental SSE parser for the /ask streaming endpoint.
 *
 * Consumes a ReadableStream of text/event-stream frames and invokes
 * callbacks for each interesting event:
 *   onSources(sources)  — the retrieved contexts (array)
 *   onDelta(text)       — an answer text chunk
 *   onError(message)    — a streamed error event
 * Returns when the stream ends or [DONE] is received.
 */
export async function consumeSse(
  body: ReadableStream<Uint8Array>,
  handlers: {
    onSources: (sources: unknown) => void;
    onDelta: (text: string) => void;
    onError: (message: string) => void;
    /** Full deterministic source list (the "Visa källor" list), one entry
     *  per citing document. */
    onAllSources?: (sources: unknown) => void;
    /** A reasoning/thinking token arrived. Fired before the first content
     *  delta so the UI can show "modellen tänker" instead of a silent wait. */
    onReasoning?: (text: string) => void;
  },
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let currentEvent = "";

  const tryParse = (json: string) => {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      // A frame may have event: + data: lines. After handling the data line
      // of a named event, reset so a following bare "data:" (OpenAI delta)
      // isn't misattributed to the previous event.
      let handledNamed = false;
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          const data = line.slice(5).trim();
          if (data === "[DONE]") return;
          if (currentEvent === "sources") {
            const srcs = tryParse(data);
            if (srcs) handlers.onSources(srcs);
            handledNamed = true;
          } else if (currentEvent === "all_sources") {
            const srcs = tryParse(data);
            if (srcs) handlers.onAllSources?.(srcs);
            handledNamed = true;
          } else if (currentEvent === "error") {
            const err = tryParse(data);
            handlers.onError(err?.body ?? data);
            handledNamed = true;
          } else if (currentEvent === "validation" || currentEvent === "query_id") {
            handledNamed = true;
          } else {
            const obj = tryParse(data);
            const delta = obj?.choices?.[0]?.delta;
            if (delta?.content) {
              handlers.onDelta(delta.content);
            } else if (delta?.reasoning) {
              handlers.onReasoning?.(delta.reasoning);
            }
          }
        }
      }
      if (handledNamed) currentEvent = "";
    }
  }
}
